import r from '../Utils/fetch'

const w: any = window
const endPoint = `${w.API_BILLING_URL}`

/**
 * Factures des comptes facturés HORS Stripe (sous contrat). Saisies à la main en
 * attendant la récupération automatique depuis Pennylane, qui alimentera la même
 * table avec `source = pennylane`.
 *
 * ⚠️ Marquer une facture payée n'est pas un geste comptable inoffensif : l'API émet
 * `payment.succeeded`, et api-partners crée la commission du partenaire attribué.
 *
 * ⚠️ Montants en CENTIMES, comme partout chez Stripe. Un float sur de l'argent finit
 * par perdre un centime au change, et personne ne sait dire lequel.
 */
export interface IClientInvoice {
	id: number
	billingAccountId: string
	/** Numéro tel qu'il figure sur la facture. Unique : il porte l'idempotence du moteur de commission. */
	number: string
	/** Date d'émission, « YYYY-MM-DD » ou RFC3339. Sert à lire, jamais à commissionner. */
	issuedAt: string
	/** HT, en centimes. Seul montant saisi : c'est l'assiette de la commission. */
	amountExcludingTaxCents: number
	currency: string
	paid: boolean
	/** Encaissement : c'est LUI qui ancre la fenêtre de commissionnement et le hold. */
	paidAt?: string | null
	source: 'manual' | 'pennylane'
	pennylaneId?: string
	createdAt: string
}

/** ⚠️ Pas de statut de paiement : une facture naît impayée, on la marque payée ensuite. */
export interface IClientInvoiceInput {
	number: string
	issuedAt: string
	amountExcludingTaxCents: number
	currency?: string
}

const base = (billingAccountId: string) =>
	`${endPoint}/v1/admin/billing-accounts/${billingAccountId}/client-invoices`

export const listClientInvoices = (billingAccountId: string): Promise<IClientInvoice[]> =>
	r({ url: base(billingAccountId), method: 'GET' })

export const createClientInvoice = (
	billingAccountId: string,
	data: IClientInvoiceInput
): Promise<IClientInvoice> => r({ url: base(billingAccountId), method: 'POST', data })

/** Émet `payment.succeeded` → commission partenaire. `paidAt` vide = maintenant. */
export const markClientInvoicePaid = (
	billingAccountId: string,
	invoiceId: number,
	paidAt?: string
): Promise<IClientInvoice> =>
	r({ url: `${base(billingAccountId)}/${invoiceId}/paid`, method: 'POST', data: { paidAt } })

/** ⚠️ Ne retire PAS la commission déjà créée : à corriger à la main sur la tirelire. */
export const markClientInvoiceUnpaid = (
	billingAccountId: string,
	invoiceId: number
): Promise<IClientInvoice> =>
	r({ url: `${base(billingAccountId)}/${invoiceId}/paid`, method: 'DELETE' })

/** Refusé par l'API sur une facture payée : la repasser en impayée d'abord. */
export const deleteClientInvoice = (
	billingAccountId: string,
	invoiceId: number
): Promise<{ id: number }> =>
	r({ url: `${base(billingAccountId)}/${invoiceId}`, method: 'DELETE' })

/** Centimes → « 1 234,56 € ». */
export const formatCents = (cents: number, currency = 'eur') =>
	new Intl.NumberFormat('fr-FR', {
		style: 'currency',
		currency: (currency || 'eur').toUpperCase(),
	}).format((cents || 0) / 100)

/**
 * « YYYY-MM-DD » pour l'affichage.
 *
 * ⚠️ On COUPE la chaîne, on ne construit pas de Date : `new Date('2026-03-12')` est lu
 * en UTC puis rendu en heure locale, et la date recule d'un jour à l'ouest de Greenwich
 * — pour certains utilisateurs seulement, ce qui rend le bug introuvable.
 */
export const formatInvoiceDay = (raw?: string | null) => (raw ? raw.slice(0, 10) : '—')

/**
 * Rattachement du compte à son client Pennylane.
 *
 * ⚠️ C'est `pennylane_short_id` — le `customer.id` de l'API v2 — et PAS `pennylane_id`,
 * qui contient un UUID hérité de l'intégration de 2026-04 et n'apparaît nulle part dans
 * l'API actuelle. Se tromper de colonne n'échoue pas : l'import ne trouve jamais de
 * facture, en silence, et le partenaire n'est jamais commissionné.
 *
 * On le trouve dans l'URL de la fiche client Pennylane (`/customers/<id>`).
 */
export const getPennylaneCustomer = (billingAccountId: string): Promise<{ pennylaneCustomerId: number }> =>
	r({ url: `${endPoint}/v1/admin/billing-accounts/${billingAccountId}/pennylane`, method: 'GET' })

/** 0 détache le compte : l'import l'ignorera, sans toucher aux factures déjà importées. */
export const setPennylaneCustomer = (
	billingAccountId: string,
	pennylaneCustomerId: number
): Promise<{ pennylaneCustomerId: number }> =>
	r({
		url: `${endPoint}/v1/admin/billing-accounts/${billingAccountId}/pennylane`,
		method: 'PUT',
		data: { pennylaneCustomerId },
	})
